import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { CorporateTaxService } from '../../../services/corporateTax.service';
import { Account } from '../../../interfaces/accounting.interface';
import { CorporateTaxSettings, TaxPayment, TaxPaymentKind } from '../../../interfaces/corporateTax.interface';
import { useToast } from '../../../context/ToastContext';
import { formatMoney, humanizeEnum } from '../../../utils/format';
import { formatDate, toLocalIsoDate } from '../../../utils/dateUtils';
import { leafAccounts } from '../../../utils/postingAccounts';
import AccountAutocomplete from '../../common/AccountAutocomplete';
import ConfirmDialog from '../../common/ConfirmDialog';

interface Props { institutionId: string; settings: CorporateTaxSettings | null; accounts: Account[]; canPost: boolean }

const KINDS: TaxPaymentKind[] = ['ADVANCE_INSTALLMENT', 'RETURN_PAYMENT', 'OTHER'];

/** Challan payments: advance tax goes to an AIT account, return payments settle income tax payable. */
const PaymentsTab: React.FC<Props> = ({ institutionId, settings, accounts, canPost }) => {
    const toast = useToast();
    const [rows, setRows] = useState<TaxPayment[]>([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [kind, setKind] = useState<TaxPaymentKind>('ADVANCE_INSTALLMENT');
    const [date, setDate] = useState(toLocalIsoDate());
    const [amount, setAmount] = useState('');
    const [bank, setBank] = useState('');
    const [ait, setAit] = useState('');
    const [challan, setChallan] = useState('');
    const [challanDate, setChallanDate] = useState(toLocalIsoDate());
    const [notes, setNotes] = useState('');
    const [toReverse, setToReverse] = useState<TaxPayment | null>(null);

    const postable = useMemo(() => leafAccounts(accounts), [accounts]);
    // Corporate tax is paid in BDT: never offer a foreign-currency (FC / ERQ) account (the server refuses it too). The
    // account list carries each GL account's own foreignCurrencyCode (FC bank rows and the chart's FC cash/bank accounts).
    const bdtPostable = useMemo(() => postable.filter(a => !a.foreignCurrencyCode), [postable]);
    const aitOptions = useMemo(() => postable.filter(a => settings?.aitAccounts.some(x => x.accountId === a.id)), [postable, settings]);

    const load = useCallback(async () => {
        try { setRows(await CorporateTaxService.listPayments(institutionId)); }
        catch (e) { toast.showApiError(e, 'Failed to load tax payments'); }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            await CorporateTaxService.createPayment(institutionId, {
                kind, paymentDate: date, amount: Number(amount), bankGlAccountId: bank,
                aitAccountId: kind === 'ADVANCE_INSTALLMENT' ? (ait || null) : null,
                challanNumber: challan, challanDate, notes: notes || null,
            });
            toast.showSuccess('Tax payment posted');
            setOpen(false);
            await load();
        } catch (e) { toast.showApiError(e, 'Failed to post the payment'); }
        finally { setBusy(false); }
    };

    const reverse = async () => {
        if (!toReverse) return;
        setBusy(true);
        try {
            await CorporateTaxService.reversePayment(institutionId, toReverse.id);
            toast.showSuccess('Payment reversed');
            setToReverse(null);
            await load();
        } catch (e) { toast.showApiError(e, 'Failed to reverse the payment'); }
        finally { setBusy(false); }
    };

    const valid = Number(amount) > 0 && bank !== '' && challan.trim() !== '' && date !== '' && challanDate !== ''
        && (kind !== 'ADVANCE_INSTALLMENT' || ait !== '');

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>{canPost && <Button data-help="accounting.corpTax.pay.record" variant="contained" onClick={() => { setAmount(''); setChallan(''); setNotes(''); setOpen(true); }}>Record payment</Button>}</Box>
            <TableContainer component={Paper}>
                <Table data-help-section="accounting.corpTax.pay.table" size="small" aria-label="Tax payments">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="accounting.corpTax.pay.col.date">Date</TableCell><TableCell data-help="accounting.corpTax.pay.col.kind">Kind</TableCell><TableCell data-help="accounting.corpTax.pay.col.challan">Challan</TableCell>
                            <TableCell data-help="accounting.corpTax.pay.col.amount" align="right">Amount</TableCell><TableCell data-help="accounting.corpTax.pay.col.journal">Journal</TableCell><TableCell data-help="accounting.corpTax.pay.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(p => (
                            <TableRow key={p.id}>
                                <TableCell>{formatDate(p.paymentDate)}</TableCell>
                                <TableCell>{humanizeEnum(p.kind)}</TableCell>
                                <TableCell>{p.challanNumber} ({formatDate(p.challanDate)})</TableCell>
                                <TableCell align="right">{formatMoney(p.amount)}</TableCell>
                                <TableCell>{p.journalEntryId
                                    ? <Link component={RouterLink} to={`/accounting/journal-entries/${p.journalEntryId}`}>View journal</Link> : '-'}</TableCell>
                                <TableCell>{p.reversedAt ? 'Reversed' : 'Posted'}</TableCell>
                                <TableCell>{canPost && !p.reversedAt && <Button data-help="accounting.corpTax.pay.action.reverse" size="small" color="error" onClick={() => setToReverse(p)}>Reverse</Button>}</TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && <TableRow><TableCell colSpan={7}>No tax payments yet.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog data-help-page="accounting.corpTax.pay.form.page" open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Record tax payment</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField data-help="accounting.corpTax.pay.form.kind" select label="Kind" value={kind} fullWidth sx={{ mt: 1 }} onChange={e => setKind(e.target.value as TaxPaymentKind)}>
                        {KINDS.map(k => <MenuItem key={k} value={k}>{humanizeEnum(k)}</MenuItem>)}
                    </TextField>
                    <TextField data-help="accounting.corpTax.pay.form.date" label="Payment date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                    <TextField data-help="accounting.corpTax.pay.form.amount" label="Amount" type="number" value={amount} fullWidth onChange={e => setAmount(e.target.value)} />
                    <AccountAutocomplete data-help="accounting.corpTax.pay.form.bank" accounts={bdtPostable} value={bank} onChange={setBank} label="Paid from (bank / cash account)" fullWidth required />
                    {kind === 'ADVANCE_INSTALLMENT' && (
                        <AccountAutocomplete data-help="accounting.corpTax.pay.form.ait" accounts={aitOptions} value={ait} onChange={setAit} label="Advance tax (AIT) account" fullWidth required
                            helperText={aitOptions.length === 0 ? 'Configure AIT accounts in Settings first' : undefined} />
                    )}
                    {kind !== 'ADVANCE_INSTALLMENT' && (
                        <TextField data-help="accounting.corpTax.pay.form.debitedTo" label="Debited to" disabled fullWidth value="Income tax payable (from Settings)" />
                    )}
                    <TextField data-help="accounting.corpTax.pay.form.challanNo" label="Challan number" value={challan} fullWidth onChange={e => setChallan(e.target.value)} />
                    <TextField data-help="accounting.corpTax.pay.form.challanDate" label="Challan date" type="date" value={challanDate} fullWidth InputLabelProps={{ shrink: true }} onChange={e => setChallanDate(e.target.value)} />
                    <TextField data-help="accounting.corpTax.pay.form.notes" label="Notes" value={notes} fullWidth multiline minRows={2} onChange={e => setNotes(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button data-help="accounting.corpTax.pay.form.cancel" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button data-help="accounting.corpTax.pay.post" variant="contained" disabled={busy || !valid} onClick={save}>Post payment</Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={toReverse !== null} title="Reverse this payment?" confirmLabel="Reverse" confirmColor="error"
                message="Posts an exact reversing journal for the challan payment." submitting={busy}
                onCancel={() => setToReverse(null)} onConfirm={reverse} />
        </Box>
    );
};

export default PaymentsTab;
